import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { getErrorMessage } from '../services/errorMessages';
import SignatureCanvas from 'react-signature-canvas';
import { FiPlay, FiPause, FiSquare, FiCoffee, FiFileText, FiClock, FiPlus, FiX, FiEye, FiPrinter, FiCalendar, FiTrash2, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import ConfirmModal from '../components/ConfirmModal';
import Calendar from '../components/Calendar';
import './UserDashboard.css';

const UserDashboard = () => {
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [breakElapsed, setBreakElapsed] = useState(0);
  const timerRef = useRef(null);

  // Templates, clients, activities
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [activities, setActivities] = useState([]);
  const [filledList, setFilledList] = useState([]);

  // Fill form
  const [showFill, setShowFill] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [headerValues, setHeaderValues] = useState({});
  const [rowData, setRowData] = useState([]);
  const [fullRowData, setFullRowData] = useState({});
  const [fillNote, setFillNote] = useState('');
  const [fillStartTime, setFillStartTime] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // Signature
  const [showSignModal, setShowSignModal] = useState(null);
  const sigRef = useRef(null);

  // View
  const [showView, setShowView] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  // Calendar fill
  const [calendarWeekStart, setCalendarWeekStart] = useState(null);
  const [calendarDays, setCalendarDays] = useState({});

  // Client detail
  const [showClientDetail, setShowClientDetail] = useState(null);
  const [clientAttachments, setClientAttachments] = useState([]);
  const [clientDateFilter, setClientDateFilter] = useState('');
  const [clientDetailLoading, setClientDetailLoading] = useState(false);

  // Work history
  const [workStats, setWorkStats] = useState({ today: 0, week: 0, month: 0, total: 0 });
  const [workSessions, setWorkSessions] = useState([]);
  const [workDates, setWorkDates] = useState([]);
  const [historyDate, setHistoryDate] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [sessRes, tmplRes, clRes, actRes, filledRes] = await Promise.all([
        api.get('/work/active'),
        api.get('/templates'),
        api.get('/clients'),
        api.get('/activities'),
        api.get('/filled-attachments/my'),
      ]);
      setSession(sessRes.data);
      setTemplates(tmplRes.data);
      setClients(clRes.data);
      setActivities(actRes.data);
      setFilledList(filledRes.data);
    } catch { /* ignored */ }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchWorkStats = useCallback(async (date) => {
    try {
      const params = date ? `?date=${date}` : '';
      const { data } = await api.get(`/work/my-stats${params}`);
      setWorkStats(data.stats);
      setWorkSessions(data.sessions);
      setWorkDates(data.workDates);
    } catch { /* ignored */ }
  }, []);

  useEffect(() => { fetchWorkStats(historyDate); }, [fetchWorkStats, historyDate]);

  // Re-fetch stats when session changes (start/end work)
  useEffect(() => { fetchWorkStats(historyDate); }, [session?.status]);

  const formatHours = (seconds) => {
    if (!seconds || seconds <= 0) return '0სთ 0წთ';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h === 0) return `${m}წთ`;
    return `${h}სთ ${m}წთ`;
  };

  // Timer
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!session || session.status === 'completed') return;

    timerRef.current = setInterval(() => {
      const start = new Date(session.started_at).getTime();
      const now = Date.now();
      let totalBreak = 0;
      (session.breaks || []).forEach((b) => {
        if (b.ended_at) {
          totalBreak += new Date(b.ended_at).getTime() - new Date(b.started_at).getTime();
        } else {
          totalBreak += now - new Date(b.started_at).getTime();
        }
      });
      setElapsed(Math.floor((now - start) / 1000));
      setBreakElapsed(Math.floor(totalBreak / 1000));
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [session]);

  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  const startWork = () => {
    setConfirmAction({
      title: 'სამუშაოს დაწყება',
      message: 'ნამდვილად გსურთ სამუშაოს დაწყება?',
      type: 'warning',
      confirmText: 'დაწყება',
      onConfirm: async () => {
        try {
          const { data } = await api.post('/work/start');
          setSession({ ...data, breaks: [] });
        } catch { /* ignored */ }
        setConfirmAction(null);
      },
    });
  };

  const endWork = () => {
    // Check unfilled templates
    const currentSessionId = session?.id;
    const filledInSession = filledList.filter((fa) => fa.session_id === currentSessionId);
    const filledTemplateIds = new Set(filledInSession.map((fa) => fa.template_id));
    const unfilled = todayTemplates.filter((t) => !filledTemplateIds.has(t.id));

    // Block if any template is unfilled
    if (unfilled.length > 0) {
      setConfirmAction({
        title: 'დანართები შეუვსებელია',
        message: `ყველა დანართი უნდა იყოს შევსებული:\n${unfilled.map((t) => `• ${t.title}`).join('\n')}`,
        type: 'delete',
        confirmText: 'გასაგებია',
        onConfirm: () => setConfirmAction(null),
      });
      return;
    }

    setConfirmAction({
      title: 'სამუშაოს დასრულება',
      message: 'ყველა დანართი შევსებულია. ნამდვილად გსურთ სამუშაოს დასრულება?',
      type: 'warning',
      confirmText: 'დასრულება',
      onConfirm: async () => {
        try {
          await api.put('/work/end');
          setSession(null); setElapsed(0); setBreakElapsed(0); fetchData();
        } catch (err) {
          const msg = getErrorMessage(err);
          setConfirmAction({
            title: 'შეცდომა',
            message: msg,
            type: 'delete',
            confirmText: 'კარგი',
            onConfirm: () => setConfirmAction(null),
          });
          return;
        }
        setConfirmAction(null);
      },
    });
  };

  const hideAttachment = (fa) => {
    setConfirmAction({
      title: 'დანართის წაშლა',
      message: `ნამდვილად გსურთ "${fa.template_title || 'დანართი'}" წაშლა?`,
      type: 'delete',
      confirmText: 'წაშლა',
      onConfirm: async () => {
        try {
          await api.put(`/filled-attachments/${fa.id}/hide`);
          fetchData();
        } catch (err) {
          const msg = getErrorMessage(err);
          setConfirmAction({
            title: 'შეცდომა', message: msg, type: 'delete', confirmText: 'კარგი',
            onConfirm: () => setConfirmAction(null),
          });
          return;
        }
        setConfirmAction(null);
      },
    });
  };

  const startBreak = () => {
    setConfirmAction({
      title: 'შესვენება',
      message: 'ნამდვილად გსურთ შესვენებაზე გასვლა?',
      type: 'warning',
      confirmText: 'შესვენება',
      onConfirm: async () => {
        try {
          const { data } = await api.post('/work/break/start');
          setSession((s) => ({ ...s, status: 'break', breaks: [...(s.breaks || []), data] }));
        } catch { /* ignored */ }
        setConfirmAction(null);
      },
    });
  };

  const endBreak = async () => {
    try {
      await api.put('/work/break/end');
      setSession((s) => {
        const breaks = [...(s.breaks || [])];
        const last = breaks.length - 1;
        if (last >= 0 && !breaks[last].ended_at) breaks[last] = { ...breaks[last], ended_at: new Date().toISOString() };
        return { ...s, status: 'active', breaks };
      });
    } catch { /* ignored */ }
  };

  // Calendar helpers
  const getMonday = (d) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    date.setDate(date.getDate() + diff);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const getWeekDates = (mondayDate, allowedDays) => {
    const days = {};
    (allowedDays || [0,1,2,3,4,5,6]).forEach((dayIdx) => {
      const d = new Date(mondayDate);
      d.setDate(d.getDate() + dayIdx);
      days[dayIdx] = d;
    });
    return days;
  };

  const DAY_NAMES = ['ორშაბათი','სამშაბათი','ოთხშაბათი','ხუთშაბათი','პარასკევი','შაბათი','კვირა'];

  const formatDateShort = (d) => {
    return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}`;
  };

  // Fill attachment
  const openFillForm = (template) => {
    setSelectedTemplate(template);
    const hv = {};
    (template.header_fields || []).forEach((f, i) => {
      if (f.type === 'auto_user') hv[i] = `${user.first_name} ${user.last_name} - ${user.personal_number}`;
      else if (f.type === 'client_dropdown' && clients.length === 1) hv[i] = String(clients[0].id);
      else hv[i] = '';
    });
    setHeaderValues(hv);

    if (template.type === 'calendar') {
      const monday = getMonday(new Date());
      monday.setDate(monday.getDate() + 7); // Next week's Monday
      setCalendarWeekStart(monday);
      const days = {};
      (template.allowed_days || [0,1,2,3,4,5,6]).forEach((dayIdx) => {
        days[dayIdx] = [{}]; // One empty entry per day
      });
      setCalendarDays(days);
      setRowData([]);
      setFullRowData({});
    } else if (template.type === 'weekly') {
      const monday = getMonday(new Date());
      monday.setDate(monday.getDate() + 7); // Next week's Monday
      setCalendarWeekStart(monday);
      // Weekly plan: start with 1 empty row
      setCalendarDays({ 0: {} });
      setRowData([]);
      setFullRowData({});
    } else {
      setRowData([createEmptyRow(template)]);
      const frd = {};
      (template.columns || []).forEach((c, i) => { if (c.fullRow) frd[i] = ['']; });
      setFullRowData(frd);
      setCalendarWeekStart(null);
      setCalendarDays({});
    }

    setFillNote('');
    setFillStartTime(new Date().toISOString());
    setShowFill(true);
  };

  const formatDateTime = (date) => {
    const d = String(date.getDate()).padStart(2, '0');
    const mo = String(date.getMonth() + 1).padStart(2, '0');
    const h = String(date.getHours()).padStart(2, '0');
    const mi = String(date.getMinutes()).padStart(2, '0');
    const sec = String(date.getSeconds()).padStart(2, '0');
    return {
      date: `${d}/${mo}/${date.getFullYear()}`,
      time: `${h}:${mi}:${sec}`,
      full: `${d}/${mo}/${date.getFullYear()} ${h}:${mi}:${sec}`,
    };
  };

  const getNow = () => formatDateTime(new Date());

  const createEmptyRow = (tmpl) => {
    const row = {};
    (tmpl.columns || []).forEach((c, i) => {
      if (c.fullRow) return;
      row[i] = '';
    });
    return row;
  };

  const addRow = () => setRowData([...rowData, createEmptyRow(selectedTemplate)]);
  const updateRow = (ri, ci, val) => {
    const u = [...rowData]; u[ri] = { ...u[ri], [ci]: val }; setRowData(u);
  };
  const removeRow = (ri) => {
    if (rowData.length === 1) return;
    setRowData(rowData.filter((_, i) => i !== ri));
  };

  const saveSignature = () => {
    if (sigRef.current && !sigRef.current.isEmpty()) {
      const dataURL = sigRef.current.toDataURL();
      if (showSignModal.fullRow) {
        const { ci, vi } = showSignModal;
        const items = [...(fullRowData[ci] || [''])];
        items[vi] = dataURL;
        setFullRowData({ ...fullRowData, [ci]: items });
      } else {
        const { ri, ci } = showSignModal;
        updateRow(ri, ci, dataURL);
      }
    }
    setShowSignModal(null);
  };

  const getClientId = () => {
    const hf = selectedTemplate.header_fields || [];
    for (let i = 0; i < hf.length; i++) {
      if (hf[i].type === 'client_dropdown' && headerValues[i]) return parseInt(headerValues[i]);
    }
    return null;
  };

  const submitAttachment = async () => {
    // Get server time for accurate timestamps
    let serverNow;
    try {
      const { data } = await api.get('/work/server-time');
      serverNow = new Date(data.now);
    } catch {
      serverNow = new Date();
    }
    const nowFormatted = formatDateTime(serverNow);
    const cols = selectedTemplate.columns || [];

    // Calculate working duration: session start to now, minus breaks
    let workDuration = 0;
    if (session) {
      const sessionStart = new Date(session.started_at).getTime();
      let totalBreak = 0;
      (session.breaks || []).forEach((b) => {
        if (b.ended_at) {
          totalBreak += new Date(b.ended_at).getTime() - new Date(b.started_at).getTime();
        }
      });
      workDuration = Math.floor((serverNow.getTime() - sessionStart - totalBreak) / 1000);
    } else {
      workDuration = Math.floor((serverNow.getTime() - new Date(fillStartTime).getTime()) / 1000);
    }
    const durationStr = formatTime(workDuration > 0 ? workDuration : 0);

    // Session start time formatted
    const sessionStartFormatted = session ? formatDateTime(new Date(session.started_at)) : formatDateTime(serverNow);

    // Auto-fill duration, datetime, and worktime columns in rows
    const finalRows = rowData.map((row) => {
      const r = { ...row };
      cols.forEach((c, i) => {
        if (c.fullRow) return;
        if (c.type === 'duration') r[i] = durationStr;
        if (c.type === 'datetime') r[i] = nowFormatted.full;
        if (c.type === 'worktime') r[i] = `${sessionStartFormatted.time} - ${nowFormatted.time}`;
      });
      return r;
    });

    // Auto-fill duration, datetime, worktime in fullRowData
    const finalFullRows = { ...fullRowData };
    cols.forEach((c, i) => {
      if (c.fullRow && c.type === 'duration') {
        finalFullRows[i] = [durationStr];
      }
      if (c.fullRow && c.type === 'datetime') {
        finalFullRows[i] = [nowFormatted.full];
      }
      if (c.fullRow && c.type === 'worktime') {
        finalFullRows[i] = [`${sessionStartFormatted.time} - ${nowFormatted.time}`];
      }
    });

    const isCalendar = selectedTemplate.type === 'calendar';
    const isWeekly = selectedTemplate.type === 'weekly';

    try {
      const payload = {
        template_id: selectedTemplate.id,
        client_id: getClientId(),
        header_values: headerValues,
        note: fillNote,
        started_at: fillStartTime,
        duration_seconds: workDuration > 0 ? workDuration : 0,
        session_id: session?.id || null,
      };

      if (isCalendar) {
        payload.calendar_data = {
          weekStart: calendarWeekStart?.toISOString()?.split('T')[0],
          days: calendarDays,
        };
        payload.row_data = [{}]; // placeholder for validation
      } else if (isWeekly) {
        payload.calendar_data = {
          type: 'weekly',
          weekStart: calendarWeekStart?.toISOString()?.split('T')[0],
          days: calendarDays,
        };
        payload.row_data = [{}]; // placeholder for validation
      } else {
        payload.row_data = finalRows;
        payload.full_row_data = finalFullRows;
      }

      await api.post('/filled-attachments', payload);
      // Check if all templates are now filled in this session
      const updatedFilled = await api.get('/filled-attachments/my');
      setFilledList(updatedFilled.data);
      const filledInSession = new Set(updatedFilled.data.filter((fa) => fa.session_id === session?.id).map((fa) => fa.template_id));
      const allDone = todayTemplates.every((t) => filledInSession.has(t.id));

      setShowConfirm(false);
      setShowFill(false);

      if (allDone && session) {
        try { await api.put('/work/end'); } catch { /* ignored */ }
        setSession(null);
        setElapsed(0);
        setBreakElapsed(0);
      }
      fetchData();
    } catch { /* ignored */ }
  };

  const openView = async (fa) => {
    try {
      const { data } = await api.get(`/filled-attachments/${fa.id}`);
      setViewData(data);
      setShowView(true);
    } catch { /* ignored */ }
  };

  const openClientDetail = async (client) => {
    setShowClientDetail(client);
    setClientDateFilter('');
    setClientDetailLoading(true);
    try {
      const { data } = await api.get(`/filled-attachments/by-client/${client.id}`);
      setClientAttachments(data);
    } catch { /* ignored */ }
    finally { setClientDetailLoading(false); }
  };

  const filteredClientAttachments = clientAttachments.filter((fa) => {
    if (!clientDateFilter) return true;
    const faDate = new Date(fa.completed_at).toISOString().split('T')[0];
    return faDate === clientDateFilter;
  });

  // Filter templates by active + today's day + sort by priority
  const todayDay = (() => { const d = new Date().getDay(); return d === 0 ? 6 : d - 1; })(); // 0=Mon..6=Sun
  const todayTemplates = templates
    .filter((t) => {
      if (t.is_active === false) return false;
      const days = t.allowed_days || [0,1,2,3,4,5,6];
      return days.includes(todayDay);
    })
    .sort((a, b) => (a.priority || 0) - (b.priority || 0));

  // Check for missed templates that should auto-open
  const autoOpenRef = useRef(false);
  useEffect(() => {
    if (!session || session.status !== 'active' || autoOpenRef.current) return;
    const missedTemplates = templates.filter((t) => {
      if (t.is_active === false) return false;
      if (!t.auto_open_missed) return false;
      const days = t.allowed_days || [0,1,2,3,4,5,6];
      // Check yesterday
      const yesterday = todayDay === 0 ? 6 : todayDay - 1;
      if (!days.includes(yesterday)) return false;
      // Check if filled yesterday
      const yDate = new Date();
      yDate.setDate(yDate.getDate() - 1);
      const yStr = yDate.toISOString().split('T')[0];
      const filled = filledList.some((fa) => fa.template_id === t.id && fa.completed_at && fa.completed_at.startsWith(yStr));
      return !filled;
    });
    if (missedTemplates.length > 0) {
      autoOpenRef.current = true;
      openFillForm(missedTemplates[0]);
    }
  }, [session, templates, filledList]);

  const printAttachment = () => {
    window.print();
  };

  const workingTime = elapsed - breakElapsed;

  return (
    <div className="page user-dashboard">
      {/* Timer section */}
      <div className="timer-section">
        <div className="timer-card">
          <div className="timer-greeting">გამარჯობა, {user.first_name}!</div>
          {!session ? (
            <div className="timer-start">
              <button className="start-btn" onClick={startWork}><FiPlay /> სამუშაოს დაწყება</button>
            </div>
          ) : (
            <>
              <div className="timer-display">
                <div className="timer-main">
                  <span className="timer-label">სამუშაო დრო</span>
                  <span className="timer-value">{formatTime(workingTime > 0 ? workingTime : 0)}</span>
                </div>
                {breakElapsed > 0 && (
                  <div className="timer-break-info">
                    <FiCoffee /> შესვენება: {formatTime(breakElapsed)}
                  </div>
                )}
              </div>
              <div className="timer-status">
                {session.status === 'break' ? (
                  <span className="status-badge status-break"><FiCoffee /> შესვენებაზე</span>
                ) : (
                  <span className="status-badge status-active"><FiClock /> მუშაობს</span>
                )}
              </div>
              <div className="timer-actions">
                {session.status === 'active' ? (
                  <button className="break-btn" onClick={startBreak}><FiCoffee /> შესვენება</button>
                ) : (
                  <button className="resume-btn" onClick={endBreak}><FiPlay /> გაგრძელება</button>
                )}
                <button className="end-btn" onClick={endWork}><FiSquare /> დასრულება</button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Work History - Collapsible */}
      <div className={`section wh-section ${historyOpen ? 'wh-open' : ''}`}>
        <div className="wh-toggle" onClick={() => setHistoryOpen(!historyOpen)}>
          <div className="wh-toggle-left">
            <FiClock className="wh-toggle-icon" />
            <span className="wh-toggle-title">ნამუშევარი საათები</span>
          </div>
          <div className="wh-toggle-right">
            <span className="wh-toggle-preview">{formatHours(workStats.today)}</span>
            {historyOpen ? <FiChevronUp className="wh-chevron" /> : <FiChevronDown className="wh-chevron" />}
          </div>
        </div>
        {historyOpen && (
          <div className="wh-body">
            <div className="wh-stats-grid">
              <div className="wh-stat-card wh-today">
                <span className="wh-stat-label">დღეს</span>
                <span className="wh-stat-value">{formatHours(workStats.today)}</span>
              </div>
              <div className="wh-stat-card wh-week">
                <span className="wh-stat-label">კვირა</span>
                <span className="wh-stat-value">{formatHours(workStats.week)}</span>
              </div>
              <div className="wh-stat-card wh-month">
                <span className="wh-stat-label">თვე</span>
                <span className="wh-stat-value">{formatHours(workStats.month)}</span>
              </div>
              <div className="wh-stat-card wh-total">
                <span className="wh-stat-label">სულ</span>
                <span className="wh-stat-value">{formatHours(workStats.total)}</span>
              </div>
            </div>
            <div className="wh-calendar-row">
              <Calendar
                value={historyDate}
                onChange={(val) => setHistoryDate(val)}
                placeholder="აირჩიეთ თარიღი"
                highlightDates={workDates}
              />
              {historyDate && (
                <button className="wh-clear-btn" onClick={() => setHistoryDate('')}><FiX size={14} /> გასუფთავება</button>
              )}
            </div>
            <div className="wh-sessions">
              {workSessions.length === 0 ? (
                <div className="empty-hint" style={{ textAlign: 'center', padding: 16 }}>
                  {historyDate ? 'ამ თარიღზე სესიები არ არის' : 'დღეს სესიები არ არის'}
                </div>
              ) : (
                workSessions.map((s) => {
                  const start = new Date(s.started_at);
                  const end = s.ended_at ? new Date(s.ended_at) : null;
                  const totalSec = end
                    ? Math.round((end - start) / 1000 - Number(s.break_seconds))
                    : Math.round((Date.now() - start) / 1000 - Number(s.break_seconds));
                  return (
                    <div key={s.id} className={`wh-session-card ${s.status !== 'completed' ? 'wh-active' : ''}`}>
                      <div className="wh-session-time">
                        <span className="wh-session-start">{start.toLocaleTimeString('ka-GE', { hour: '2-digit', minute: '2-digit', hour12: false })}</span>
                        <span className="wh-session-sep">—</span>
                        <span className="wh-session-end">{end ? end.toLocaleTimeString('ka-GE', { hour: '2-digit', minute: '2-digit', hour12: false }) : '...'}</span>
                      </div>
                      <div className="wh-session-details">
                        <span className="wh-session-duration">{formatHours(totalSec > 0 ? totalSec : 0)}</span>
                        {Number(s.break_seconds) > 0 && (
                          <span className="wh-session-break"><FiCoffee size={12} /> {formatHours(Math.round(Number(s.break_seconds)))}</span>
                        )}
                      </div>
                      {s.status !== 'completed' && (
                        <span className={`wh-session-status ${s.status === 'break' ? 'wh-on-break' : ''}`}>
                          {s.status === 'break' ? 'შესვენებაზე' : 'მუშაობს'}
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Quick actions - always show active templates */}
      {todayTemplates.length > 0 && (() => {
        const currentSessionId = session?.id;
        const filledTodayIds = new Set(filledList.filter((fa) => fa.session_id === currentSessionId).map((fa) => fa.template_id));
        return (
          <div className="section">
            <h3 className="section-title">დანართის შევსება</h3>
            <div className="template-pick-grid">
              {todayTemplates.map((t, i) => {
                const isFilled = filledTodayIds.has(t.id);
                return (
                  <button key={t.id} className={`template-pick-btn ${isFilled ? 'filled' : ''}`} onClick={() => openFillForm(t)} disabled={isFilled || !session || session.status === 'completed'}>
                    <span className="tpb-num">{i + 1}</span>
                    {(t.type === 'calendar' || t.type === 'weekly') ? <FiCalendar /> : <FiFileText />} {t.title}
                    {isFilled && <span className="tpb-done">✓</span>}
                  </button>
                );
              })}
              {!session && <span className="empty-hint">დანართის შესავსებად დაიწყეთ სამუშაო</span>}
            </div>
          </div>
        );
      })()}

      {/* Assigned clients */}
      <div className="section">
        <h3 className="section-title">ჩემი ბენეფიციარები</h3>
        <div className="my-clients-list">
          {clients.map((c) => (
            <div key={c.id} className="my-client-card" onClick={() => openClientDetail(c)}>
              <div className="mc-avatar">{c.first_name[0]}{c.last_name[0]}</div>
              <div className="mc-info">
                <span className="mc-name">{c.first_name} {c.last_name}</span>
                <span className="mc-pn">{c.personal_number}</span>
              </div>
              <FiEye className="mc-arrow" />
            </div>
          ))}
          {clients.length === 0 && <span className="empty-hint">ბენეფიციარები არ არის მიმაგრებული</span>}
        </div>
      </div>

      {/* Filled attachments history - grouped by session */}
      <div className="section">
        <h3 className="section-title">შევსებული დანართები</h3>
        <div className="filled-list">
          {(() => {
            // Group by session_id
            const groups = [];
            const sessionMap = new Map();
            filledList.forEach((fa) => {
              const key = fa.session_id || `no-session-${fa.id}`;
              if (!sessionMap.has(key)) {
                sessionMap.set(key, []);
                groups.push(key);
              }
              sessionMap.get(key).push(fa);
            });
            if (groups.length === 0) return <div className="empty-hint">შევსებული დანართები არ არის</div>;
            return groups.map((key) => {
              const items = sessionMap.get(key);
              const first = items[0];
              const dateStr = new Date(first.completed_at).toLocaleDateString('ka-GE', { day: '2-digit', month: '2-digit', year: 'numeric' });
              return (
                <div key={key} className="filled-session-group">
                  <div className="fsg-header">
                    <FiClock className="fsg-icon" />
                    <span className="fsg-date">{dateStr}</span>
                    <span className="fsg-count">{items.length} დანართი</span>
                  </div>
                  {items.map((fa) => (
                    <div key={fa.id} className="filled-card" onClick={() => openView(fa)}>
                      <div className="fc-icon"><FiFileText /></div>
                      <div className="fc-info">
                        <span className="fc-title">{fa.template_title || 'დანართი'}</span>
                        <span className="fc-meta">
                          {fa.client_first_name && `${fa.client_first_name} ${fa.client_last_name} | `}
                          {new Date(fa.completed_at).toLocaleString('ka-GE', { hour12: false })}
                        </span>
                      </div>
                      <div className="fc-actions"><FiEye /></div>
                    </div>
                  ))}
                </div>
              );
            });
          })()}
        </div>
      </div>

      {/* Fill form modal */}
      {showFill && selectedTemplate && (
        <div className="modal-overlay" onClick={() => setShowFill(false)}>
          <div className="modal attachment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedTemplate.title}</h3>
              <button className="btn-icon" onClick={() => setShowFill(false)}><FiX /></button>
            </div>
            <div className="fill-form">
              {/* Header fields */}
              {(selectedTemplate.header_fields || []).map((f, i) => (
                <div key={i} className="form-group">
                  <label>{f.label}</label>
                  {f.type === 'client_dropdown' ? (
                    <select value={headerValues[i] || ''} onChange={(e) => setHeaderValues({ ...headerValues, [i]: e.target.value })}>
                      <option value="">აირჩიეთ</option>
                      {clients.map((c) => (
                        <option key={c.id} value={c.id}>{c.first_name} {c.last_name} - {c.personal_number}</option>
                      ))}
                    </select>
                  ) : f.type === 'auto_user' ? (
                    <input value={headerValues[i] || ''} readOnly className="auto-field" />
                  ) : (
                    <input value={headerValues[i] || ''} onChange={(e) => setHeaderValues({ ...headerValues, [i]: e.target.value })} />
                  )}
                </div>
              ))}

              {/* Calendar fill form */}
              {selectedTemplate.type === 'calendar' && calendarWeekStart && (() => {
                const cols = (selectedTemplate.columns || []).map((c, i) => ({ ...c, idx: i }));
                const weekDates = getWeekDates(calendarWeekStart, selectedTemplate.allowed_days);
                const nextMonday = getMonday(new Date());
                nextMonday.setDate(nextMonday.getDate() + 7);
                const canGoPrev = calendarWeekStart.getTime() > nextMonday.getTime();
                const changeWeek = (dir) => {
                  if (dir === -1 && !canGoPrev) return;
                  const d = new Date(calendarWeekStart);
                  d.setDate(d.getDate() + dir * 7);
                  setCalendarWeekStart(d);
                };
                const updateCalEntry = (dayIdx, entryIdx, colIdx, val) => {
                  const u = { ...calendarDays };
                  u[dayIdx] = [...(u[dayIdx] || [{}])];
                  u[dayIdx][entryIdx] = { ...u[dayIdx][entryIdx], [colIdx]: val };
                  setCalendarDays(u);
                };
                const addCalEntry = (dayIdx) => {
                  const u = { ...calendarDays };
                  u[dayIdx] = [...(u[dayIdx] || []), {}];
                  setCalendarDays(u);
                };
                const removeCalEntry = (dayIdx, entryIdx) => {
                  const u = { ...calendarDays };
                  u[dayIdx] = (u[dayIdx] || []).filter((_, i) => i !== entryIdx);
                  if (u[dayIdx].length === 0) u[dayIdx] = [{}];
                  setCalendarDays(u);
                };

                const endOfWeek = new Date(calendarWeekStart);
                endOfWeek.setDate(endOfWeek.getDate() + 6);

                return (
                  <div className="cal-fill">
                    {/* Week picker */}
                    <div className="cal-week-picker">
                      <button type="button" className={`cal-week-nav ${!canGoPrev ? 'disabled' : ''}`} onClick={() => changeWeek(-1)} disabled={!canGoPrev}>‹</button>
                      <span className="cal-week-label">
                        {formatDateShort(calendarWeekStart)} — {formatDateShort(endOfWeek)}, {calendarWeekStart.getFullYear()}
                      </span>
                      <button type="button" className="cal-week-nav" onClick={() => changeWeek(1)}>›</button>
                    </div>

                    {/* Day sections */}
                    {Object.keys(weekDates).sort((a, b) => a - b).map((dayIdx) => {
                      const date = weekDates[dayIdx];
                      const entries = calendarDays[dayIdx] || [{}];
                      return (
                        <div key={dayIdx} className="cal-day-section">
                          <div className="cal-day-header">
                            <span className="cal-day-name">{DAY_NAMES[dayIdx]}</span>
                            <span className="cal-day-date">{date.toLocaleDateString('ka-GE')}</span>
                            <span className="cal-day-count">{entries.filter(e => Object.values(e).some(v => v)).length} ჩანაწერი</span>
                          </div>
                          <div className="cal-day-entries">
                            {entries.map((entry, ei) => (
                              <div key={ei} className="cal-entry">
                                <span className="cal-entry-num">{ei + 1}</span>
                                <div className="cal-entry-fields">
                                  {cols.map((c) => (
                                    <div key={c.idx} className="cal-entry-field">
                                      <label className="cal-field-label">{c.name}</label>
                                      {c.type === 'activity' ? (
                                        <select className="fill-input" value={entry[c.idx] || ''} onChange={(e) => updateCalEntry(parseInt(dayIdx), ei, c.idx, e.target.value)}>
                                          <option value="">აირჩიეთ განხორციელებული აქტივობები</option>
                                          {activities.map((a) => (<option key={a.id} value={a.name}>{a.name}</option>))}
                                        </select>
                                      ) : c.type === 'dropdown' ? (
                                        <select className="fill-input" value={entry[c.idx] || ''} onChange={(e) => updateCalEntry(parseInt(dayIdx), ei, c.idx, e.target.value)}>
                                          <option value="">აირჩიეთ</option>
                                          {(c.options || []).filter(o => o.trim()).map((o, oi) => (<option key={oi} value={o}>{o}</option>))}
                                        </select>
                                      ) : c.type === 'datetime' || c.type === 'worktime' || c.type === 'duration' ? (
                                        <span className="auto-field-label">ავტო</span>
                                      ) : (
                                        <input className="fill-input" value={entry[c.idx] || ''} onChange={(e) => updateCalEntry(parseInt(dayIdx), ei, c.idx, e.target.value)} placeholder={c.name} />
                                      )}
                                    </div>
                                  ))}
                                </div>
                                {entries.length > 1 && (
                                  <button type="button" className="btn-icon btn-danger btn-xs" onClick={() => removeCalEntry(parseInt(dayIdx), ei)}><FiX /></button>
                                )}
                              </div>
                            ))}
                            <button type="button" className="cal-add-entry" onClick={() => addCalEntry(parseInt(dayIdx))}>
                              <FiPlus /> ჩანაწერი
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Weekly plan fill form — same style as standard */}
              {selectedTemplate.type === 'weekly' && calendarWeekStart && (() => {
                const cols = (selectedTemplate.columns || []).map((c, i) => ({ ...c, idx: i }));
                const autoTypes = ['datetime', 'worktime', 'duration'];
                const normalCols = cols.filter(c => !c.fullRow);
                const editableCols = normalCols.filter(c => !autoTypes.includes(c.type));
                const allFullRow = editableCols.length === 0;
                // Rows are stored as numeric keys in calendarDays (exclude _fullRow)
                const rowKeys = Object.keys(calendarDays).filter(k => k !== '_fullRow').sort((a, b) => a - b);
                const rowCount = rowKeys.length;

                const updateWeeklyField = (dayIdx, colIdx, val) => {
                  const u = { ...calendarDays };
                  u[dayIdx] = { ...(u[dayIdx] || {}), [colIdx]: val };
                  setCalendarDays(u);
                };

                const addWeeklyRow = () => {
                  const nextKey = rowKeys.length > 0 ? Math.max(...rowKeys.map(Number)) + 1 : 0;
                  setCalendarDays({ ...calendarDays, [nextKey]: {} });
                };

                const removeWeeklyRow = (key) => {
                  const u = { ...calendarDays };
                  delete u[key];
                  setCalendarDays(u);
                };

                // Check if last row has all non-auto, non-fullRow, non-signature cols filled
                const lastRowData = rowKeys.length > 0 ? (calendarDays[rowKeys[rowKeys.length - 1]] || {}) : {};
                const canAddRow = normalCols.every(c => {
                  if (autoTypes.includes(c.type) || c.type === 'signature') return true;
                  return !!(lastRowData[c.idx]?.toString().trim());
                });

                const endOfWeek = new Date(calendarWeekStart);
                endOfWeek.setDate(endOfWeek.getDate() + 6);

                const renderWeeklyCell = (c, dayIdx, dayData) => {
                  if (c.type === 'signature') {
                    return dayData[c.idx] ? (
                      <img src={dayData[c.idx]} alt="" className="sig-thumb" onClick={() => setShowSignModal({ weekly: true, dayIdx: parseInt(dayIdx), ci: c.idx })} />
                    ) : (
                      <button type="button" className="sign-btn" onClick={() => setShowSignModal({ weekly: true, dayIdx: parseInt(dayIdx), ci: c.idx })}>✍</button>
                    );
                  }
                  if (c.type === 'activity') {
                    return (
                      <select className="fill-input" value={dayData[c.idx] || ''} onChange={(e) => updateWeeklyField(parseInt(dayIdx), c.idx, e.target.value)}>
                        <option value="">აირჩიეთ განხორციელებული აქტივობები</option>
                        {activities.map((a) => (<option key={a.id} value={a.name}>{a.name}</option>))}
                      </select>
                    );
                  }
                  if (c.type === 'dropdown') {
                    const opts = (c.options || []).filter(o => o.trim());
                    return (
                      <select className="fill-input" value={dayData[c.idx] || ''} onChange={(e) => updateWeeklyField(parseInt(dayIdx), c.idx, e.target.value)}>
                        <option value="">აირჩიეთ</option>
                        {opts.map((o, oi) => (<option key={oi} value={o}>{o}</option>))}
                      </select>
                    );
                  }
                  if (c.type === 'datetime') return <span className="auto-field-label">ავტო: თარიღი/დრო</span>;
                  if (c.type === 'worktime') return <span className="auto-field-label">ავტო: დაწყ/დასრ</span>;
                  if (c.type === 'duration') return <span className="auto-field-label">ავტო: ხანგრძლივობა</span>;
                  return <input className="fill-input" value={dayData[c.idx] || ''} onChange={(e) => updateWeeklyField(parseInt(dayIdx), c.idx, e.target.value)} />;
                };

                const renderWeeklyFullRowCell = (c) => {
                  const val = calendarDays._fullRow?.[c.idx] || '';
                  if (c.type === 'signature') {
                    return (
                      <div className="fullrow-cell">
                        <div className="fullrow-cell-item">
                          {val ? <img src={val} alt="" className="sig-thumb" onClick={() => setShowSignModal({ weeklyFullRow: true, ci: c.idx })} />
                            : <button type="button" className="sign-btn" onClick={() => setShowSignModal({ weeklyFullRow: true, ci: c.idx })}>✍</button>}
                        </div>
                      </div>
                    );
                  }
                  if (c.type === 'datetime' || c.type === 'worktime' || c.type === 'duration') {
                    return <span className="auto-field-label">ავტო</span>;
                  }
                  return (
                    <div className="fullrow-cell">
                      <div className="fullrow-cell-item">
                        <input className="fill-input" value={val} onChange={(e) => {
                          const u = { ...calendarDays };
                          u._fullRow = { ...(u._fullRow || {}), [c.idx]: e.target.value };
                          setCalendarDays(u);
                        }} placeholder={c.name} />
                      </div>
                    </div>
                  );
                };

                return (
                  <>
                    <div className="weekly-week-label">
                      კვირა: {formatDateShort(calendarWeekStart)} — {formatDateShort(endOfWeek)}, {calendarWeekStart.getFullYear()}
                    </div>
                    <div className="fill-table-wrap">
                      <table className="fill-table">
                        <thead><tr><th>№</th>
                          {cols.map((c) => (<th key={c.idx} style={c.width ? {width: `${c.width}%`} : undefined}>{c.name}{c.required && <span style={{color:'#e53e3e'}}> *</span>}</th>))}
                          {!allFullRow && <th></th>}
                        </tr></thead>
                        <tbody>
                          {rowKeys.map((dayIdx, di) => {
                            const dayData = calendarDays[dayIdx] || {};
                            return (
                              <tr key={dayIdx}>
                                <td className="row-num">{di + 1}</td>
                                {cols.map((c) => (
                                  <td key={c.idx}>{renderWeeklyCell(c, dayIdx, dayData)}</td>
                                ))}
                                {!allFullRow && <td><button type="button" className="btn-icon btn-danger btn-sm" onClick={() => removeWeeklyRow(dayIdx)} disabled={rowCount <= 1}><FiX /></button></td>}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {!allFullRow && <button type="button" className="add-row-btn" onClick={addWeeklyRow} disabled={!canAddRow}><FiPlus /> სტრიქონი</button>}
                  </>
                );
              })()}

              {/* Standard Table - all columns, fullRow merged */}
              {selectedTemplate.type === 'standard' && (() => {
                const cols = (selectedTemplate.columns || []).map((c, i) => ({ ...c, idx: i }));
                const rowCount = rowData.length;
                const renderCell = (c, row, ri) => {
                  if (c.type === 'signature') {
                    return row[c.idx] ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <img src={row[c.idx]} alt="" className="sig-thumb" onClick={() => setShowSignModal({ ri, ci: c.idx })} />
                        <button type="button" className="btn-icon btn-danger btn-xs" onClick={() => updateRow(ri, c.idx, '')}><FiX /></button>
                      </div>
                    ) : (
                      <button type="button" className="sign-btn" onClick={() => setShowSignModal({ ri, ci: c.idx })}>✍</button>
                    );
                  }
                  if (c.type === 'activity') {
                    return (
                      <select className="fill-input" value={row[c.idx] || ''} onChange={(e) => updateRow(ri, c.idx, e.target.value)}>
                        <option value="">აირჩიეთ განხორციელებული აქტივობები</option>
                        {activities.map((a) => (<option key={a.id} value={a.name}>{a.name}</option>))}
                      </select>
                    );
                  }
                  if (c.type === 'dropdown') {
                    const opts = (c.options || []).filter(o => o.trim());
                    return (
                      <select className="fill-input" value={row[c.idx] || ''} onChange={(e) => updateRow(ri, c.idx, e.target.value)}>
                        <option value="">აირჩიეთ</option>
                        {opts.map((o, oi) => (<option key={oi} value={o}>{o}</option>))}
                      </select>
                    );
                  }
                  if (c.type === 'datetime') {
                    return <span className="auto-field-label">ავტო: თარიღი/დრო</span>;
                  }
                  if (c.type === 'worktime') {
                    return <span className="auto-field-label">ავტო: დაწყ/დასრ</span>;
                  }
                  if (c.type === 'duration') return <span className="auto-field-label">ავტო: ხანგრძლივობა</span>;
                  return <input className="fill-input" value={row[c.idx] || ''} onChange={(e) => updateRow(ri, c.idx, e.target.value)} />;
                };

                const renderFullRowCell = (c) => {
                  const items = fullRowData[c.idx] || [''];
                  return (
                    <div className="fullrow-cell">
                      {items.map((val, vi) => (
                        <div key={vi} className="fullrow-cell-item">
                          {c.type === 'activity' ? (
                            <select className="fill-input" value={val} onChange={(e) => { const u = [...items]; u[vi] = e.target.value; setFullRowData({ ...fullRowData, [c.idx]: u }); }}>
                              <option value="">აირჩიეთ განხორციელებული აქტივობები</option>
                              {activities.map((a) => (<option key={a.id} value={a.name}>{a.name}</option>))}
                            </select>
                          ) : c.type === 'dropdown' ? (
                            <select className="fill-input" value={val} onChange={(e) => { const u = [...items]; u[vi] = e.target.value; setFullRowData({ ...fullRowData, [c.idx]: u }); }}>
                              <option value="">აირჩიეთ</option>
                              {(c.options || []).filter(o => o.trim()).map((o, oi) => (<option key={oi} value={o}>{o}</option>))}
                            </select>
                          ) : c.type === 'signature' ? (
                            val ? <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <img src={val} alt="" className="sig-thumb" onClick={() => setShowSignModal({ fullRow: true, ci: c.idx, vi })} />
                                <button type="button" className="btn-icon btn-danger btn-xs" onClick={() => { const u = [...items]; u[vi] = ''; setFullRowData({ ...fullRowData, [c.idx]: u }); }}><FiX /></button>
                              </div>
                              : <button type="button" className="sign-btn" onClick={() => setShowSignModal({ fullRow: true, ci: c.idx, vi })}>✍</button>
                          ) : c.type === 'datetime' || c.type === 'worktime' || c.type === 'duration' ? (
                            <span className="auto-field-label">ავტო</span>
                          ) : (
                            <input className="fill-input" value={val} onChange={(e) => { const u = [...items]; u[vi] = e.target.value; setFullRowData({ ...fullRowData, [c.idx]: u }); }} placeholder={c.name} />
                          )}
                          {items.length > 1 && !['datetime','worktime','duration'].includes(c.type) && <button type="button" className="btn-icon btn-danger btn-xs" onClick={() => setFullRowData({ ...fullRowData, [c.idx]: items.filter((_, i) => i !== vi) })}><FiX /></button>}
                        </div>
                      ))}
                      {!['datetime','worktime','duration'].includes(c.type) && <button type="button" className="fullrow-add-btn" onClick={() => setFullRowData({ ...fullRowData, [c.idx]: [...items, ''] })}>+</button>}
                    </div>
                  );
                };

                return (
                  <>
                    <div className="fill-table-wrap">
                      <table className="fill-table">
                        <thead><tr><th>№</th>
                          {cols.map((c) => (<th key={c.idx} style={c.width ? {width: `${c.width}%`} : undefined}>{c.name}{c.required && <span style={{color:'#e53e3e'}}> *</span>}</th>))}
                          <th></th>
                        </tr></thead>
                        <tbody>
                          {rowData.map((row, ri) => (
                            <tr key={ri}>
                              <td className="row-num">{ri + 1}</td>
                              {cols.map((c) => {
                                if (c.fullRow && ri === 0) {
                                  return <td key={c.idx} rowSpan={rowCount} className="fullrow-td">{renderFullRowCell(c)}</td>;
                                }
                                if (c.fullRow) return null;
                                return <td key={c.idx}>{renderCell(c, row, ri)}</td>;
                              })}
                              {rowData.length > 1 && <td><button type="button" className="btn-icon btn-danger btn-sm" onClick={() => removeRow(ri)}><FiX /></button></td>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <button type="button" className="add-row-btn" onClick={addRow}><FiPlus /> სტრიქონი</button>
                  </>
                );
              })()}

              {selectedTemplate.has_note && (
                <div className="form-group" style={{ marginTop: 16 }}>
                  <label>შენიშვნა</label>
                  <textarea className="fill-textarea" value={fillNote} onChange={(e) => setFillNote(e.target.value)} rows={3} />
                </div>
              )}

              <button type="button" className="btn-primary btn-full" style={{ marginTop: 16 }} onClick={() => setShowConfirm(true)} disabled={(() => {
                const cols = (selectedTemplate.columns || []).map((c, i) => ({ ...c, idx: i }));
                const autoTypes = ['datetime', 'worktime', 'duration'];
                const requiredCols = cols.filter(c => c.required && !autoTypes.includes(c.type));
                if (requiredCols.length === 0) {
                  // Check header fields only
                  const hf = selectedTemplate.header_fields || [];
                  for (let i = 0; i < hf.length; i++) {
                    if (hf[i].type !== 'auto_user' && !headerValues[i]?.toString().trim()) return true;
                  }
                  return false;
                }
                // Check header fields (except auto_user)
                const hf = selectedTemplate.header_fields || [];
                for (let i = 0; i < hf.length; i++) {
                  if (hf[i].type !== 'auto_user' && !headerValues[i]?.toString().trim()) return true;
                }
                // Standard template
                if (selectedTemplate.type === 'standard' || !selectedTemplate.type) {
                  for (const row of rowData) {
                    for (const c of requiredCols.filter(c => !c.fullRow)) {
                      if (!row[c.idx]?.toString().trim()) return true;
                    }
                  }
                  // Check fullRow required columns
                  for (const c of requiredCols.filter(c => c.fullRow)) {
                    const items = fullRowData[c.idx] || [''];
                    if (items.every(v => !v?.toString().trim())) return true;
                  }
                }
                // Calendar template
                if (selectedTemplate.type === 'calendar') {
                  for (const dayIdx of Object.keys(calendarDays)) {
                    const entries = calendarDays[dayIdx] || [{}];
                    for (const entry of entries) {
                      for (const c of requiredCols) {
                        if (!entry[c.idx]?.toString().trim()) return true;
                      }
                    }
                  }
                }
                // Weekly template
                if (selectedTemplate.type === 'weekly') {
                  const rowKeys = Object.keys(calendarDays).filter(k => k !== '_fullRow');
                  for (const key of rowKeys) {
                    const dayData = calendarDays[key] || {};
                    for (const c of requiredCols) {
                      if (!dayData[c.idx]?.toString().trim()) return true;
                    }
                  }
                }
                return false;
              })()}>
                დადასტურება
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm popup */}
      {showConfirm && (
        <ConfirmModal
          title="დანართის დადასტურება"
          message="ნამდვილად ადასტურებთ დანართის შევსებას? დადასტურების შემდეგ ცვლილება შეუძლებელია."
          type="warning"
          confirmText="დადასტურება"
          onConfirm={submitAttachment}
          onCancel={() => setShowConfirm(false)}
        />
      )}

      {/* Signature modal */}
      {showSignModal && (
        <div className="modal-overlay" style={{ zIndex: 3001 }}>
          <div className="modal sign-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h3>ხელმოწერა</h3>
              <button className="btn-icon" onClick={() => setShowSignModal(null)}><FiX /></button></div>
            <div className="sign-area">
              <SignatureCanvas ref={sigRef} canvasProps={{ className: 'sign-canvas' }} penColor="#1a1a2e" />
              <div className="sign-actions">
                <button type="button" className="btn-secondary" onClick={() => sigRef.current?.clear()}>გასუფთავება</button>
                <button type="button" className="btn-primary" onClick={saveSignature}>შენახვა</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Client detail modal */}
      {showClientDetail && (
        <div className="modal-overlay" onClick={() => setShowClientDetail(null)}>
          <div className="modal attachment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{showClientDetail.first_name} {showClientDetail.last_name}</h3>
              <button className="btn-icon" onClick={() => setShowClientDetail(null)}><FiX /></button>
            </div>
            <div className="client-detail-content">
              {/* Client info */}
              <div className="cd-info-grid">
                <div className="cd-info-item">
                  <span className="cd-label">პირადი ნომერი</span>
                  <span className="cd-value">{showClientDetail.personal_number}</span>
                </div>
                {showClientDetail.mobile && (
                  <div className="cd-info-item">
                    <span className="cd-label">მობილური</span>
                    <span className="cd-value">{showClientDetail.mobile}</span>
                  </div>
                )}
                {showClientDetail.address && (
                  <div className="cd-info-item">
                    <span className="cd-label">მისამართი</span>
                    <span className="cd-value">{showClientDetail.address}</span>
                  </div>
                )}
                {showClientDetail.birth_date && (
                  <div className="cd-info-item">
                    <span className="cd-label">დაბადების თარიღი</span>
                    <span className="cd-value">{new Date(showClientDetail.birth_date).toLocaleDateString('ka-GE')}</span>
                  </div>
                )}
                {showClientDetail.gender && (
                  <div className="cd-info-item">
                    <span className="cd-label">სქესი</span>
                    <span className="cd-value">{showClientDetail.gender === 'male' ? 'მამრობითი' : 'მდედრობითი'}</span>
                  </div>
                )}
              </div>

              {/* Attachments for this client */}
              <div className="cd-attachments-header">
                <h4>შევსებული დანართები ({filteredClientAttachments.length})</h4>
                <Calendar
                  value={clientDateFilter}
                  onChange={(val) => setClientDateFilter(val)}
                  placeholder="თარიღით ძიება"
                />
              </div>

              {clientDetailLoading ? (
                <div className="loading">იტვირთება...</div>
              ) : (
                <div className="cd-attachments-list">
                  {filteredClientAttachments.map((fa) => (
                    <div key={fa.id} className="filled-card" onClick={() => { setShowClientDetail(null); openView(fa); }}>
                      <div className="fc-icon"><FiFileText /></div>
                      <div className="fc-info">
                        <span className="fc-title">{fa.template_title || 'დანართი'}</span>
                        <span className="fc-meta">{new Date(fa.completed_at).toLocaleString('ka-GE', { hour12: false })}</span>
                        {fa.duration_seconds && (
                          <span className="fc-duration"><FiClock /> {formatTime(fa.duration_seconds)}</span>
                        )}
                      </div>
                      <FiEye className="fc-actions" />
                    </div>
                  ))}
                  {filteredClientAttachments.length === 0 && (
                    <div className="empty-hint" style={{ padding: 20, textAlign: 'center' }}>
                      {clientDateFilter ? 'ამ თარიღზე დანართები არ არის' : 'დანართები არ არის'}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* View filled attachment */}
      {showView && viewData && (
        <div className="modal-overlay" onClick={() => setShowView(false)}>
          <div className="modal attachment-modal print-area" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header no-print">
              <h3>{viewData.template_title}</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-icon" onClick={printAttachment} title="ბეჭდვა"><FiPrinter /></button>
                <button className="btn-icon" onClick={() => setShowView(false)}><FiX /></button>
              </div>
            </div>
            <div className="paper">
              <div className="paper-title-right">{viewData.template_title}</div>
              {viewData.template_main_title && <div className="paper-center"><div className="paper-main-title">{viewData.template_main_title}</div></div>}
              {viewData.template_subtitle && <div className="paper-center"><div className="paper-subtitle">{viewData.template_subtitle}</div></div>}
              <div className="paper-fields">
                {(viewData.template_header_fields || []).map((f, i) => (
                  <div key={i} className="paper-field">
                    <span className="paper-field-label">{f.label}</span>
                    <span className="paper-field-value">
                      {f.type === 'client_dropdown' ? `${viewData.client_first_name || ''} ${viewData.client_last_name || ''} - ${viewData.client_pn || ''}` :
                       f.type === 'auto_user' ? `${viewData.user_first_name || ''} ${viewData.user_last_name || ''} - ${viewData.user_pn || ''}` :
                       (viewData.header_values || {})[i] || ''}
                    </span>
                  </div>
                ))}
              </div>
              {(() => {
                const cols = (viewData.template_columns || []).map((c, i) => ({ ...c, idx: i }));
                const rd = viewData.row_data || {};

                // Calendar view
                if (rd.type === 'calendar') {
                  const days = rd.days || {};
                  const weekStart = rd.weekStart;
                  const dayNames = ['ორშაბათი','სამშაბათი','ოთხშაბათი','ხუთშაბათი','პარასკევი','შაბათი','კვირა'];
                  return (
                    <div className="paper-calendar-view">
                      {weekStart && <div className="paper-week-label">კვირა: {weekStart}</div>}
                      {Object.keys(days).sort((a, b) => a - b).map((dayIdx) => {
                        const entries = days[dayIdx] || [];
                        const monday = weekStart ? new Date(weekStart) : null;
                        let dateStr = '';
                        if (monday) {
                          const d = new Date(monday);
                          d.setDate(d.getDate() + parseInt(dayIdx));
                          dateStr = d.toLocaleDateString('ka-GE');
                        }
                        return (
                          <div key={dayIdx} className="paper-cal-day">
                            <div className="paper-cal-day-header">
                              <strong>{dayNames[dayIdx]}</strong>
                              {dateStr && <span>{dateStr}</span>}
                            </div>
                            {entries.length > 0 && entries.some(e => Object.values(e).some(v => v)) ? (
                              <table className="paper-table" style={{marginBottom: 0}}>
                                <thead><tr><th className="paper-col-num">№</th>
                                  {cols.map((c) => <th key={c.idx} style={c.width ? {width: `${c.width}%`} : undefined}>{c.name}</th>)}
                                </tr></thead>
                                <tbody>
                                  {entries.filter(e => Object.values(e).some(v => v)).map((entry, ei) => (
                                    <tr key={ei}>
                                      <td className="paper-col-num">{ei + 1}</td>
                                      {cols.map((c) => <td key={c.idx}>{entry[c.idx] || ''}</td>)}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <div style={{padding: '8px 12px', color: '#94a3b8', fontSize: 13}}>ჩანაწერები არ არის</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                // Weekly plan view
                if (rd.type === 'weekly') {
                  const days = rd.days || {};
                  const weekStart = rd.weekStart;
                  const visibleCols = cols.filter(c => c.type !== 'datetime' && c.type !== 'worktime' && c.type !== 'duration');
                  const dayKeys = Object.keys(days).filter(k => k !== '_fullRow').sort((a, b) => a - b);
                  // Format week range
                  let weekLabel = weekStart || '';
                  if (weekStart) {
                    const ws = new Date(weekStart);
                    const we = new Date(ws);
                    we.setDate(we.getDate() + 6);
                    weekLabel = `${ws.toLocaleDateString('ka-GE')} — ${we.toLocaleDateString('ka-GE')}`;
                  }

                  return (
                    <div className="paper-table-wrap">
                      {weekStart && <div className="paper-week-label" style={{marginBottom: 12}}>კვირა: {weekLabel}</div>}
                      <table className="paper-table">
                        <thead><tr>
                          {visibleCols.map((c) => <th key={c.idx} className={c.fullRow ? 'paper-col-fullrow' : ''} style={c.width ? {width: `${c.width}%`} : undefined}>{c.name}</th>)}
                        </tr></thead>
                        <tbody>
                          {dayKeys.map((dayIdx, di) => {
                            const dayData = days[dayIdx] || {};
                            return (
                              <tr key={dayIdx}>
                                {visibleCols.map((c) => {
                                  // For fullRow columns, get value from first row's data or _fullRow
                                  if (c.fullRow && di === 0) {
                                    const val = days._fullRow?.[c.idx] || (dayKeys.length > 0 ? (days[dayKeys[0]]?.[c.idx] || '') : '');
                                    return (
                                      <td key={c.idx} rowSpan={dayKeys.length} className="paper-cell-fullrow" style={{ verticalAlign: 'top', fontSize: 13, whiteSpace: 'pre-wrap' }}>
                                        {c.type === 'signature' && val ? <img src={val} alt="" style={{ height: 25, objectFit: 'contain' }} /> : val}
                                      </td>
                                    );
                                  }
                                  if (c.fullRow) return null;
                                  return (
                                    <td key={c.idx} style={{ whiteSpace: 'pre-wrap' }}>
                                      {c.type === 'signature' && dayData[c.idx] ? <img src={dayData[c.idx]} alt="" style={{ height: 25, objectFit: 'contain' }} /> : (dayData[c.idx] || '')}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                }

                // Standard view
                const rows = rd.rows || (Array.isArray(rd) ? rd : []);
                const fullRows = rd.fullRows || {};
                const rowCount = Math.max(rows.length, 1);

                return (
                  <div className="paper-table-wrap">
                    <table className="paper-table">
                      <thead><tr><th className="paper-col-num">№</th>
                        {cols.map((c) => (<th key={c.idx} className={c.fullRow ? 'paper-col-fullrow' : ''} style={c.width ? {width: `${c.width}%`} : undefined}>{c.name}</th>))}
                      </tr></thead>
                      <tbody>
                        {rows.map((row, ri) => (
                          <tr key={ri}>
                            <td className="paper-col-num">{ri + 1}</td>
                            {cols.map((c) => {
                              if (c.fullRow && ri === 0) {
                                const items = fullRows[c.idx] || [];
                                return (
                                  <td key={c.idx} rowSpan={rowCount} className="paper-cell-fullrow" style={{ verticalAlign: 'middle', fontSize: 13 }}>
                                    {items.map((val, vi) => (
                                      <div key={vi} style={{ padding: '2px 0' }}>
                                        {c.type === 'signature' && val ? <img src={val} alt="" style={{ height: 25, objectFit: 'contain' }} /> : (val || '')}
                                      </div>
                                    ))}
                                  </td>
                                );
                              }
                              if (c.fullRow) return null;
                              return (
                                <td key={c.idx}>
                                  {c.type === 'signature' && row[c.idx] ? <img src={row[c.idx]} alt="" style={{ height: 25, objectFit: 'contain' }} /> : (row[c.idx] || '')}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
              {viewData.note && <div className="paper-note"><strong>შენიშვნა:</strong> {viewData.note}</div>}
              {false && viewData.duration_seconds && (
                <div className="paper-duration">ხანგრძლივობა: {formatTime(viewData.duration_seconds)}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmAction && <ConfirmModal {...confirmAction} onCancel={() => setConfirmAction(null)} />}
    </div>
  );
};

export default UserDashboard;
